import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  documents,
  policies,
  type PolicyArchiveKind,
  type PolicyDetail,
  type PolicyHistoryEntry,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { HistoryTab } from '@/shared/ui/HistoryTab';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

const rotuloDoMovimento: Record<PolicyHistoryEntry['event_kind'], string> = {
  emissao: 'Emissao',
  endosso: 'Endosso',
  cancelamento: 'Cancelamento',
  renovacao: 'Renovacao',
  alteracao: 'Alteracao',
  carga: 'Registro da carga',
};

const tiposDeArquivo: { value: PolicyArchiveKind; label: string }[] = [
  { value: 'apolice', label: 'Apolice' },
  { value: 'endosso', label: 'Endosso' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'laudo_emissao', label: 'Laudo de emissao' },
  { value: 'certificado_cancelamento', label: 'Certificado de cancelamento' },
  { value: 'outro', label: 'Outro' },
];

/** Os valores da fotografia com o nome legivel, em centavos e pontos-base. */
const camposDaFotografia: Record<string, string> = {
  number: 'Numero interno',
  external_number: 'Numero da apolice',
  proposal_number: 'Cotacao',
  status: 'Situacao',
  insured_amount_cents: 'IS',
  original_insured_amount_cents: 'IS na emissao',
  premium_cents: 'Premio',
  principal_premium_cents: 'Premio da principal',
  complementary_premium_cents: 'Premio da complementar',
  applied_rate_bps: 'Taxa aplicada',
  commission_bps: 'Comissao',
  coverage_start: 'Inicio da vigencia',
  coverage_end: 'Fim da vigencia',
  term_days: 'Prazo (dias)',
  big_risk: 'Grande risco',
  monitoring: 'Monitoramento',
  step_in: 'Retoma de objeto (Step-in)',
  coinsurance_type: 'Cosseguro',
  reason: 'Motivo',
  kind: 'Tipo',
  endorsement_number: 'Endosso',
  amount_delta_cents: 'Diferenca na IS',
  new_coverage_end: 'Novo fim da vigencia',
};

function valorDaFotografia(chave: string, valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '--';
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Nao';
  if (typeof valor === 'number' && chave.endsWith('_cents')) {
    return formatMoney({ amount: valor, currency: 'BRL', exponent: 2 });
  }
  if (typeof valor === 'number' && chave.endsWith('_bps')) {
    return formatRate(valor);
  }
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valor)) {
    return formatCivilDate(valor);
  }
  if (typeof valor === 'string' || typeof valor === 'number') return String(valor);
  return JSON.stringify(valor);
}

/**
 * Aba "Historico" da apolice (APO-094 a APO-100): os movimentos da apolice com a
 * fotografia de cada um, o autor, o aceite e os arquivos; os endossos da apolice; e a
 * trilha de alteracoes comum (P-FUN-2b).
 *
 * O aceite do registro esta a confirmar (END-006): a coluna existe e fica vazia ate o
 * aceite por emissao e endosso (P-USU-2) chegar.
 */
export function PolicyHistoryTab({ policy }: { policy: PolicyDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const [fotografia, setFotografia] = useState<PolicyHistoryEntry | null>(null);
  const [ligando, setLigando] = useState<PolicyHistoryEntry | null>(null);

  const historico = useQuery({
    queryKey: ['policy-history', policy.id],
    queryFn: ({ signal }) => policies.history(policy.id, signal),
  });
  const podeVerEndossos = can('endossos.endosso.view');
  const endossos = useQuery({
    queryKey: ['policy-endorsements', policy.id],
    queryFn: ({ signal }) => policies.endorsements(policy.id, signal),
    enabled: podeVerEndossos,
  });

  const baixar = useMutation({
    mutationFn: (documentId: string) => policies.file(policy.id, documentId),
    onSuccess: (link) =>
      window.open(enderecoDeArquivo(link.url), '_blank', 'noopener,noreferrer'),
    onError: (error: Error) => toast.error(error.message),
  });

  const podeLigar = can('apolices.apolice.update') && can('documentos.documento.view');

  return (
    <div className="flex flex-col gap-5">
      <DetailCard
        title="Historico da apolice"
        description="Os movimentos da apolice, com a fotografia de cada um e os arquivos."
        loading={historico.isPending}
        error={historico.error}
      >
        {(historico.data ?? []).length === 0 ? (
          <p className="text-sm text-ink-600">Nenhum movimento registrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-2xs font-semibold uppercase tracking-wide text-ink-500">
                  <th scope="col" className="py-2 pr-3">
                    Data
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    Movimento
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    Autor
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    Aceite
                  </th>
                  <th scope="col" className="py-2 pr-3">
                    Arquivos
                  </th>
                  <th scope="col" className="py-2">
                    <span className="sr-only">Acoes</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {(historico.data ?? []).map((registro) => (
                  <tr key={registro.id}>
                    <td className="py-2 pr-3 tabular-nums">
                      {formatDateTime(registro.created_at)}
                    </td>
                    <td className="py-2 pr-3">
                      <p className="font-medium text-ink-900">
                        {rotuloDoMovimento[registro.event_kind]}
                      </p>
                      <p className="text-2xs text-ink-500">{registro.summary}</p>
                    </td>
                    <td className="py-2 pr-3">{registro.created_label || 'sistema'}</td>
                    <td className="py-2 pr-3">
                      {registro.accepted_at
                        ? `${registro.accepted_label} em ${formatDateTime(registro.accepted_at)}`
                        : '--'}
                    </td>
                    <td className="py-2 pr-3">
                      {registro.documents.length === 0 ? (
                        '--'
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {registro.documents.map((arquivo) => (
                            <li key={arquivo.id}>
                              <button
                                type="button"
                                className="text-left text-brand-600 underline-offset-2 hover:underline"
                                onClick={() => baixar.mutate(arquivo.document_id)}
                              >
                                {arquivo.filename || arquivo.title}
                              </button>
                              <span className="block text-2xs text-ink-500">
                                {tiposDeArquivo.find(
                                  (t) => t.value === arquivo.archive_kind,
                                )?.label ?? arquivo.archive_kind}{' '}
                                - {formatDateTime(arquivo.created_at)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        <AcaoComIcone
                          acao="visualizar"
                          rotulo="Ver os valores"
                          onClick={() => setFotografia(registro)}
                        />
                        {podeLigar ? (
                          <AcaoComIcone
                            acao="vincular"
                            rotulo="Ligar arquivo"
                            onClick={() => setLigando(registro)}
                          />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailCard>

      {podeVerEndossos ? (
        <DetailCard
          title="Endossos da apolice"
          loading={endossos.isPending}
          error={endossos.error}
        >
          {(endossos.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-600">A apolice nao tem endosso.</p>
          ) : (
            <ul className="divide-y divide-line">
              {(endossos.data ?? []).map((endosso) => (
                <li
                  key={endosso.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0 text-sm">
                    <p className="font-medium text-ink-900">
                      Endosso {endosso.number} - {statusLabel('endosso', endosso.kind)}
                    </p>
                    <p className="truncate text-2xs text-ink-500">
                      {endosso.reason}
                      {endosso.amount_delta
                        ? ` - ${formatMoney(endosso.amount_delta)}`
                        : ''}
                      {endosso.new_coverage_end
                        ? ` - fim em ${formatCivilDate(endosso.new_coverage_end)}`
                        : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusBadge kind="solicitacao" value={endosso.status} />
                    <p className="mt-1 text-2xs text-ink-500">
                      {endosso.decided_at
                        ? `${endosso.decided_label} em ${formatDateTime(endosso.decided_at)}`
                        : formatDateTime(endosso.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DetailCard>
      ) : null}

      <HistoryTab
        entityType="policy"
        entityId={policy.id}
        title="Historico de alteracoes"
        description="A trilha da apolice: emissao, parcelas informadas e arquivos ligados, com o antes e o depois."
      />

      <Modal
        open={fotografia !== null}
        onClose={() => setFotografia(null)}
        title="Valores do registro"
        description={fotografia ? rotuloDoMovimento[fotografia.event_kind] : undefined}
        size="md"
      >
        {fotografia ? (
          <dl className="grid gap-3 sm:grid-cols-2">
            {Object.entries(fotografia.snapshot)
              .filter(([chave]) => chave in camposDaFotografia)
              .map(([chave, valor]) => (
                <div key={chave}>
                  <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
                    {camposDaFotografia[chave]}
                  </dt>
                  <dd className="text-sm text-ink-900">
                    {valorDaFotografia(chave, valor)}
                  </dd>
                </div>
              ))}
          </dl>
        ) : null}
      </Modal>

      {ligando ? (
        <LigarArquivo
          policyId={policy.id}
          registro={ligando}
          onClose={() => setLigando(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Liga um documento da apolice, com arquivo, a um movimento. Ate a geracao do
 * documento (P-MIN-2), a ligacao e manual.
 */
function LigarArquivo({
  policyId,
  registro,
  onClose,
}: {
  policyId: string;
  registro: PolicyHistoryEntry;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [documento, setDocumento] = useState('');
  const [tipo, setTipo] = useState<PolicyArchiveKind>('apolice');

  const disponiveis = useQuery({
    queryKey: ['documents', { owner_type: 'apolice', owner_id: policyId }],
    queryFn: ({ signal }) =>
      documents.list({ owner_type: 'apolice', owner_id: policyId, limit: 100 }, signal),
  });
  const comArquivo = (disponiveis.data?.items ?? []).filter(
    (d) => d.current_version > 0,
  );

  const ligar = useMutation({
    mutationFn: () =>
      policies.linkHistoryDocument(policyId, registro.id, {
        document_id: documento,
        archive_kind: tipo,
      }),
    onSuccess: () => {
      toast.success('Arquivo ligado ao movimento.');
      void queryClient.invalidateQueries({ queryKey: ['policy-history', policyId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Ligar arquivo ao movimento"
      description={registro.summary}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={documento === ''}
            loading={ligar.isPending}
            onClick={() => ligar.mutate()}
          >
            Ligar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {ligar.isError ? (
          <Alert tone="danger" title="Nao foi possivel ligar">
            {ligar.error.message}
          </Alert>
        ) : null}
        {comArquivo.length === 0 && !disponiveis.isPending ? (
          <p className="text-sm text-ink-600">
            A apolice nao tem documento com arquivo. Envie o arquivo na aba Documentos.
          </p>
        ) : null}
        <Select
          label="Documento da apolice"
          placeholder="Selecionar"
          value={documento}
          options={comArquivo.map((d) => ({ value: d.id, label: d.title }))}
          onChange={(event) => setDocumento(event.target.value)}
        />
        <Select
          label="Tipo do arquivo"
          value={tipo}
          options={tiposDeArquivo}
          onChange={(event) => setTipo(event.target.value as PolicyArchiveKind)}
        />
      </div>
    </Modal>
  );
}
